import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft, Loader2, PlayCircle, Lock, CheckCircle2, ShoppingCart, GraduationCap, Clock, Search, Coins, Trophy, Award } from "lucide-react";
import { toast } from "sonner";
import { fetchDetalleCurso, comprarCurso, obtenerStreamUrl, concluirLeccion, reportarProgreso, descargarCertificado } from "../lib/academia";
import { humanizeAxiosError } from "../lib/api";
import { PrecioDescuento } from "../components/PrecioDescuento";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const PLAN_LABEL = { pro: "Pro", elite: "Elite" };

export default function AcademiaCurso() {
  const { id } = useParams();
  const [curso, setCurso] = useState(null);
  const [comprando, setComprando] = useState(false);
  const [streamUrl, setStreamUrl] = useState(null);
  const [streamExterno, setStreamExterno] = useState(false);
  const [activa, setActiva] = useState(null); // lección activa
  const [cargandoVideo, setCargandoVideo] = useState(false);
  const [concluyendo, setConcluyendo] = useState(false);
  const [progresoTotal, setProgresoTotal] = useState(0);
  const [q, setQ] = useState("");
  const ultimoReporte = useRef(0);
  const [descargandoCert, setDescargandoCert] = useState(false);

  async function bajarCertificado() {
    setDescargandoCert(true);
    try { await descargarCertificado(id); toast.success("Generando tu certificado…"); }
    catch (e) { toast.error(humanizeAxiosError(e)); }
    finally { setDescargandoCert(false); }
  }

  function onTimeUpdate(e) {
    const v = e.target;
    if (!v.duration || !activa || activa.completado) return;
    const pct = Math.min(100, Math.round((v.currentTime / v.duration) * 100));
    // Llenado en vivo de la barra individual (no retrocede)
    setCurso((c) => c ? {
      ...c, modulos: c.modulos.map((m) => ({
        ...m, lecciones: m.lecciones.map((l) => l.lid === activa.lid && !l.completado ? { ...l, pct: Math.max(l.pct || 0, pct) } : l),
      })),
    } : c);
    const ahora = Date.now();
    if (ahora - ultimoReporte.current > 12000) {
      ultimoReporte.current = ahora;
      reportarProgreso(id, activa.lid, pct).then((d) => setProgresoTotal(d.total_pct)).catch(() => {});
    }
  }

  function cargar() {
    fetchDetalleCurso(id).then((c) => {
      setCurso(c);
      setProgresoTotal(c.progreso_total || 0);
    }).catch(() => setCurso(false));
  }
  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [id]);

  // lista plana de lecciones con índice global (numeración tipo Hotmart)
  const lecciones = useMemo(() => {
    if (!curso || !curso.modulos) return [];
    const out = [];
    let n = 0;
    curso.modulos.forEach((m, mi) => m.lecciones.forEach((l) => { n += 1; out.push({ ...l, modulo: m.titulo, mi, num: n }); }));
    return out;
  }, [curso]);

  const leccionesFiltradas = useMemo(() => {
    if (!q.trim()) return lecciones;
    const t = q.toLowerCase();
    return lecciones.filter((l) => l.titulo.toLowerCase().includes(t) || (l.modulo || "").toLowerCase().includes(t));
  }, [lecciones, q]);

  async function comprar() {
    setComprando(true);
    try {
      const r = await comprarCurso(id);
      if (r.url) { window.location.href = r.url; return; }
      if (r.comprado) { toast.success("¡Inscripción realizada!"); cargar(); }
    } catch (e) { toast.error(humanizeAxiosError(e)); }
    finally { setComprando(false); }
  }

  async function reproducir(lec) {
    if (!lec.puede_ver) { toast.info("Inscríbete para ver esta clase."); return; }
    if (!lec.tiene_video) { toast.info("Esta lección aún no tiene video."); return; }
    setCargandoVideo(true); setActiva(lec); setStreamUrl(null);
    try {
      const url = await obtenerStreamUrl(id, lec.lid);
      setStreamExterno(!url.includes("/api/academia/stream"));
      setStreamUrl(url);
    } catch (e) { toast.error(humanizeAxiosError(e)); }
    finally { setCargandoVideo(false); }
  }

  function embedUrl(u) {
    try {
      if (/youtube\.com|youtu\.be/.test(u)) {
        const m = u.match(/(?:v=|youtu\.be\/|embed\/)([\w-]{6,})/);
        return m ? `https://www.youtube.com/embed/${m[1]}` : u;
      }
      if (/vimeo\.com/.test(u)) {
        const m = u.match(/vimeo\.com\/(?:video\/)?(\d+)/);
        return m ? `https://player.vimeo.com/video/${m[1]}` : u;
      }
    } catch { /* noop */ }
    return u;
  }
  const esIframe = streamExterno && /youtube\.com|youtu\.be|vimeo\.com/.test(streamUrl || "");

  function aplicarProgreso(data, lid) {
    setProgresoTotal(data.total_pct);
    setCurso((c) => {
      if (!c) return c;
      const modulos = c.modulos.map((m) => ({
        ...m, lecciones: m.lecciones.map((l) => l.lid === lid ? { ...l, completado: true, pct: 100 } : l),
      }));
      return { ...c, modulos };
    });
    setActiva((a) => (a && a.lid === lid ? { ...a, completado: true, pct: 100 } : a));
  }

  async function concluir() {
    if (!activa) return;
    setConcluyendo(true);
    try {
      const data = await concluirLeccion(id, activa.lid);
      aplicarProgreso(data, activa.lid);
      toast.success("¡Lección completada! +10 puntos", { icon: "🎉" });
      if (data.total_pct === 100) toast.success("¡Felicidades! Completaste el curso 🏆");
    } catch (e) { toast.error(humanizeAxiosError(e)); }
    finally { setConcluyendo(false); }
  }

  if (curso === null) return <div className="p-10 flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={18} /> Cargando curso…</div>;
  if (curso === false) return <div className="p-10 text-center text-slate-500">Curso no encontrado. <Link to="/academia" className="text-brand-blue font-semibold">Volver</Link></div>;

  const activaCompletada = activa?.completado;

  // ---------- NO INSCRITO: vista de venta ----------
  if (!curso.inscrito) {
    return (
      <div className="px-5 md:px-8 py-8 max-w-6xl mx-auto" data-testid="academia-curso-page">
        <Link to="/academia" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800"><ChevronLeft size={16} /> Volver a la Academia</Link>
        <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-5">
            <div className="rounded-2xl overflow-hidden bg-slate-900 aspect-video flex items-center justify-center" data-testid="academia-player">
              {curso.tiene_imagen ? <img src={`${BACKEND}/api/academia/${curso.id}/imagen`} alt={curso.titulo} className="w-full h-full object-cover opacity-90" /> : <div className="text-center text-slate-400"><GraduationCap size={48} className="mx-auto" /></div>}
            </div>
            <div>
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full" style={{ color: "#EA580C", backgroundColor: "#EA580C1a" }}>{curso.categoria || "Curso"}</span>
              <h1 className="mt-3 text-2xl font-extrabold text-slate-900">{curso.titulo}</h1>
              {curso.instructor && <p className="mt-1 text-sm text-slate-500">Por {curso.instructor}{curso.nivel ? ` · Nivel ${curso.nivel}` : ""}</p>}
              <p className="mt-3 text-slate-600 leading-relaxed">{curso.descripcion}</p>
            </div>
          </div>
          <aside className="space-y-5">
            <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-5" data-testid="academia-compra-box">
              <PrecioDescuento original={curso.precio_original} final={curso.precio_final} descuentoPct={curso.descuento_pct} />
              {curso.descuento_pct > 0 && curso.ahorro > 0 && (
                <p className="mt-1 text-xs font-semibold text-emerald-600" data-testid="academia-ahorro">Ahorraste S/ {Number(curso.ahorro).toFixed(2)} por ser miembro {PLAN_LABEL[curso.plan] || "Pro"}</p>
              )}
              <button onClick={comprar} disabled={comprando} data-testid="academia-comprar-btn" className="mt-4 w-full inline-flex items-center justify-center gap-2 text-white font-semibold py-3 rounded-xl transition-opacity hover:opacity-90 disabled:opacity-60" style={{ backgroundColor: "#EA580C" }}>
                {comprando ? <Loader2 size={16} className="animate-spin" /> : <ShoppingCart size={16} />} Inscribirme al curso
              </button>
              <p className="mt-3 text-xs text-slate-400">{curso.num_modulos} módulos · {curso.num_lecciones} lecciones</p>
            </div>
            <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-5" data-testid="academia-temario">
              <h3 className="font-bold text-slate-900 mb-3">Temario</h3>
              <div className="space-y-4">
                {curso.modulos.map((m, mi) => (
                  <div key={`${mi}-${m.titulo}`} data-testid={`temario-modulo-${mi}`}>
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Módulo {mi + 1} · {m.titulo}</p>
                    <div className="mt-2 space-y-1">
                      {m.lecciones.map((l) => (
                        <button key={l.lid} onClick={() => reproducir(l)} data-testid={`leccion-${l.lid}`} className={`w-full flex items-center gap-2 text-left text-sm px-2.5 py-2 rounded-lg hover:bg-slate-50 text-slate-700 ${!l.puede_ver ? "opacity-70" : ""}`}>
                          {l.puede_ver ? <PlayCircle size={15} className="text-orange-500 flex-shrink-0" /> : <Lock size={15} className="text-slate-400 flex-shrink-0" />}
                          <span className="flex-1 truncate">{l.titulo}</span>
                          {l.gratis && <span className="text-[10px] font-bold uppercase text-emerald-600">Gratis</span>}
                          {l.duracion && <span className="text-xs text-slate-400">{l.duracion}</span>}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>
    );
  }

  // ---------- INSCRITO: Aula Virtual (dark) ----------
  return (
    <div className="-mx-5 md:-mx-8 -my-8 min-h-[calc(100vh-64px)] bg-slate-950 text-white" data-testid="academia-curso-page">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px]">
        {/* Reproductor */}
        <main className="p-5 md:p-8">
          <div className="flex items-center justify-between">
            <Link to="/academia" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-300 hover:text-white"><ChevronLeft size={16} /> Volver</Link>
            <div className="flex items-center gap-3">
              {progresoTotal >= 100 && (
                <button onClick={bajarCertificado} disabled={descargandoCert} data-testid="aula-certificado-btn" className="inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-slate-900 font-bold text-sm px-4 py-2 rounded-xl transition-colors disabled:opacity-60">
                  {descargandoCert ? <Loader2 size={15} className="animate-spin" /> : <Award size={15} />} Descargar certificado
                </button>
              )}
              <div className="inline-flex items-center gap-2 text-xs font-semibold text-emerald-400" data-testid="aula-progreso-total"><Trophy size={14} /> Progreso del curso: {progresoTotal}%</div>
            </div>
          </div>

          <div className="mt-4">
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{activa ? activa.modulo : curso.categoria}</p>
            <h1 className="text-2xl font-extrabold">{activa ? activa.titulo : curso.titulo}</h1>
          </div>

          <div className="mt-4 rounded-2xl overflow-hidden bg-black aspect-video flex items-center justify-center border border-white/5" data-testid="academia-player">
            {cargandoVideo ? (
              <Loader2 className="animate-spin text-white" size={32} />
            ) : streamUrl && esIframe ? (
              <iframe key={streamUrl} src={embedUrl(streamUrl)} title="Lección" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen className="w-full h-full" data-testid="academia-video" />
            ) : streamUrl ? (
              <video key={streamUrl} src={streamUrl} controls autoPlay controlsList="nodownload" disablePictureInPicture onContextMenu={(e) => e.preventDefault()} onTimeUpdate={onTimeUpdate} onEnded={concluir} className="w-full h-full" data-testid="academia-video" />
            ) : curso.tiene_imagen ? (
              <img src={`${BACKEND}/api/academia/${curso.id}/imagen`} alt={curso.titulo} className="w-full h-full object-cover opacity-80" />
            ) : (
              <div className="text-center text-slate-500"><GraduationCap size={48} className="mx-auto" /><p className="mt-2 text-sm">Selecciona una lección de la derecha para comenzar</p></div>
            )}
          </div>

          {/* Gamificación: Concluir */}
          <div className="mt-5 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-1 text-amber-400">
              {[1, 2, 3, 4, 5].map((s) => <Trophy key={s} size={14} className="opacity-30" />)}
            </div>
            {activa && (
              activaCompletada ? (
                <span className="inline-flex items-center gap-2 bg-emerald-500/15 text-emerald-400 font-bold px-5 py-2.5 rounded-xl" data-testid="aula-completada">
                  <CheckCircle2 size={18} /> Completada
                </span>
              ) : (
                <button onClick={concluir} disabled={concluyendo} data-testid="aula-concluir-btn" className="inline-flex items-center gap-2 bg-white text-slate-900 font-bold px-6 py-2.5 rounded-xl hover:bg-slate-100 transition-colors disabled:opacity-60">
                  {concluyendo ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={18} />} Concluir
                  <span className="inline-flex items-center gap-1 text-emerald-600 text-sm"><Coins size={14} /> +10</span>
                </button>
              )
            )}
          </div>

          <div className="mt-6 border-t border-white/10 pt-4">
            <p className="text-sm font-bold text-slate-200">Información de la clase</p>
            <p className="mt-2 text-sm text-slate-400 leading-relaxed">{curso.descripcion}</p>
            {curso.instructor && <p className="mt-2 text-xs text-slate-500">Instructor: {curso.instructor}{curso.nivel ? ` · Nivel ${curso.nivel}` : ""}</p>}
          </div>
        </main>

        {/* Barra lateral de lecciones */}
        <aside className="bg-slate-900 border-l border-white/10 p-5 lg:h-[calc(100vh-64px)] lg:overflow-y-auto" data-testid="aula-sidebar">
          <div className="mb-3">
            <div className="flex items-center justify-between text-xs text-slate-400 mb-1">
              <span>Avance total</span><span className="font-bold text-emerald-400">{progresoTotal}%</span>
            </div>
            <div className="h-2 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full bg-emerald-500 transition-all" style={{ width: `${progresoTotal}%` }} data-testid="aula-barra-total" />
            </div>
          </div>

          <div className="relative mb-4">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="aula-buscar" placeholder="Buscar contenido" className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 pr-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-white/25" />
          </div>

          <div className="space-y-2">
            {leccionesFiltradas.map((l) => {
              const isActive = activa?.lid === l.lid;
              return (
                <button key={l.lid} onClick={() => reproducir(l)} data-testid={`leccion-${l.lid}`}
                  className={`w-full flex items-center gap-3 text-left rounded-xl px-3 py-3 transition-colors ${isActive ? "bg-white/10 ring-1 ring-orange-500/40" : "hover:bg-white/5"} ${!l.puede_ver ? "opacity-70" : ""}`}>
                  <span className={`w-9 h-9 flex-shrink-0 rounded-full flex items-center justify-center text-sm font-bold ${l.completado ? "bg-emerald-500/20 text-emerald-400" : "bg-white/10 text-slate-300"}`}>
                    {l.completado ? <CheckCircle2 size={18} /> : l.num}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="flex items-center gap-2 text-sm font-semibold text-white truncate">
                      {!l.puede_ver && <Lock size={12} className="text-slate-400 flex-shrink-0" />}
                      <span className="truncate">{l.titulo}</span>
                    </span>
                    {l.completado ? (
                      <span className="mt-1 inline-flex items-center gap-1 text-[11px] font-bold text-emerald-400"><CheckCircle2 size={12} /> Completo</span>
                    ) : (
                      <span className="mt-1.5 flex items-center gap-2">
                        <span className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
                          <span className="block h-full bg-orange-500" style={{ width: `${l.pct || 0}%` }} />
                        </span>
                        <span className="text-[11px] text-slate-400 w-8 text-right">{l.pct || 0}%</span>
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </aside>
      </div>
    </div>
  );
}
